import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaSave, 
  FaTimes, 
  FaSpinner,
  FaTools,
  FaCode,
  FaDatabase,
  FaCloud,
  FaMobile
} from 'react-icons/fa';
import { uploadAPI } from '../../utils/api.js';
import { usePortfolio } from '../../context/PortfolioContext';
import VisibilityToggle from './VisibilityToggle';
import { skillsAPI } from '../../utils/api'; // Added import for skillsAPI

const SkillsEditor = () => {
  const { portfolioData, groupSkillsByCategory, addSkill, updateSkill, deleteSkill } = usePortfolio();
  const [skills, setSkills] = useState({});
  // Initialize local skills state from portfolioData
  useEffect(() => {
    // Always load full skills list for admin (include hidden)
    const loadAll = async () => {
      try {
        const response = await skillsAPI.getAll(true);
        if (response.success) {
          setSkills(groupSkillsByCategory(response.data));
        }
      } catch {}
    };
    loadAll();
  }, []);

  const reloadSkills = async () => {
    try {
      const response = await skillsAPI.getAll(true);
      if (response.success) {
        setSkills(groupSkillsByCategory(response.data));
      }
    } catch {}
  };
  const [editingSkill, setEditingSkill] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSkill, setNewSkill] = useState({
    category: '',
    name: '',
    level: 0,
    icon_url: '',
    visible: true
  });
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedCategoryOption, setSelectedCategoryOption] = useState(''); // For Add form
  const [editCategoryOption, setEditCategoryOption] = useState('');
  const [toggleLoading, setToggleLoading] = useState({});

  // Add Skill
  const handleAddSkill = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const result = await addSkill(newSkill);
      if (result.success) {
        setMessage('Skill added successfully!');
        setNewSkill({ category: '', name: '', level: 0, icon_url: '', visible: true });
      setShowAddForm(false);
        setSelectedCategoryOption('');
        await reloadSkills();
      } else {
        setMessage(result.error || 'Error adding skill');
      }
    } catch (error) {
      setMessage('Error adding skill');
    } finally {
      setIsSaving(false);
    }
  };

  // Edit Skill
  const handleUpdateSkill = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const newItem = {
        ...editingSkill,
        id: editingSkill.id,
        visible: editingSkill.visible
      };
      const result = await updateSkill(editingSkill.id, newItem);
      if (result.success) {
        setSkills(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(category => {
            updated[category] = updated[category].map(s =>
              s.id === editingSkill.id ? { ...s, ...newItem } : s
            );
          });
          return updated;
        });
        setEditingSkill(null);
        setMessage('Skill updated successfully!');
        await reloadSkills();
      } else {
        setMessage(result.error || 'Error updating skill');
      }
    } catch (error) {
      setMessage('Error updating skill');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete Skill
  const handleDeleteSkill = async (id) => {
    if (!window.confirm('Are you sure you want to delete this skill?')) return;
    setIsSaving(true);
    setMessage('');
    try {
      const result = await deleteSkill(id);
      if (result.success) {
      setMessage('Skill deleted successfully!');
      await reloadSkills();
      } else {
        setMessage(result.error || 'Error deleting skill');
      }
    } catch (error) {
      setMessage('Error deleting skill');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleVisibility = async (category, index, checked) => {
    const skill = skills[category][index];
    setToggleLoading(prev => ({ ...prev, [skill.id]: true }));
    try {
      // Optimistic UI
      setSkills(prev => ({
        ...prev,
        [category]: prev[category].map((s, i) => i === index ? { ...s, visible: checked } : s)
      }));
      // Persist only visibility using existing context helper
      await updateSkill(skill.id, { visible: checked });
      await reloadSkills();
    } catch (error) {
      // Revert optimistic change
      setSkills(prev => ({
        ...prev,
        [category]: prev[category].map((s, i) => i === index ? { ...s, visible: !checked } : s)
      }));
      // Optionally show error
    } finally {
      setToggleLoading(prev => ({ ...prev, [skill.id]: false }));
    }
  };

  // Add this function for toggling visibility in the skills list
  const handleSkillChange = (category, index, field, value) => {
    setSkills(prev => ({
      ...prev,
      [category]: prev[category].map((s, i) =>
        i === index ? { ...s, [field]: value } : s
      )
    }));
  };

  const getCategoryIcon = (category) => {
    // This function is no longer needed as category is free text
    return <FaTools />;
  };

  const getCategoryLabel = (category) => {
    // This function is no longer needed as category is free text
    return category;
  };

  // Helper to get all unique categories
  const allCategories = Object.keys(skills).filter(Boolean);

  useEffect(() => {
    if (editingSkill) {
      if (allCategories.includes(editingSkill.category)) {
        setEditCategoryOption(editingSkill.category);
      } else if (editingSkill.category) {
        setEditCategoryOption('other');
      } else {
        setEditCategoryOption('');
      }
    }
  }, [editingSkill, allCategories]);

  if (!portfolioData) {
    return (
      <div className="flex justify-center items-center h-64">
        <FaSpinner className="animate-spin text-4xl text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800 rounded-lg p-6"
    >
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-white">Skills Manager</h3>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
        >
          <FaPlus />
          Add Skill
        </motion.button>
      </div>

      {message && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`mb-4 p-3 rounded-lg ${
            message.includes('Error') 
              ? 'bg-red-500/20 text-red-300 border border-red-500/30' 
              : 'bg-green-500/20 text-green-300 border border-green-500/30'
          }`}
        >
          {message}
        </motion.div>
      )}

      {/* Add Skill Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 p-4 bg-gray-700 rounded-lg"
          >
            <h4 className="text-lg font-medium text-white mb-4">Add New Skill</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={selectedCategoryOption}
                  onChange={e => {
                    setSelectedCategoryOption(e.target.value);
                    if (e.target.value === 'other') {
                      setNewSkill({ ...newSkill, category: '' });
                    } else {
                      setNewSkill({ ...newSkill, category: e.target.value });
                    }
                  }}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                >
                  <option value="">Select Category</option>
                  {allCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                  <option value="other">Other (Add New Category)</option>
                </select>
                {/* Show text input if 'Other' is selected */}
                {selectedCategoryOption === 'other' && (
                  <input
                    type="text"
                    value={newSkill.category}
                    onChange={e => setNewSkill({ ...newSkill, category: e.target.value })}
                    className="mt-2 w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                    placeholder="Type new category"
                  />
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Skill Name
                </label>
                <input
                  type="text"
                  value={newSkill.name}
                  onChange={(e) => setNewSkill({ ...newSkill, name: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="e.g., React, Node.js"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Proficiency Level ({newSkill.level}%)
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={newSkill.level}
                  onChange={(e) => setNewSkill({ ...newSkill, level: parseInt(e.target.value) })}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Icon Image (optional)</label>
                {newSkill.icon_url && (
                  <div className="mb-2 flex items-center gap-4">
                    <img
                      src={newSkill.icon_url}
                      alt="Skill Icon"
                      className="w-12 h-12 object-cover rounded border-2 border-primary"
                      onError={e => { e.target.onerror = null; e.target.src = '/default-skill.png'; }}
                    />
                    <button
                      type="button"
                      onClick={() => setNewSkill(prev => ({ ...prev, icon_url: '' }))}
                      className="p-2 text-red-400 hover:text-red-300"
                    >
                      <FaTimes /> Remove
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={async e => {
                    const file = e.target.files[0];
                    if (file) {
                      const response = await uploadAPI.uploadImage(file);
                      if (response.success) setNewSkill(prev => ({ ...prev, icon_url: response.data.url }));
                    }
                  }}
                  className="block mt-2"
                />
              </div>
              <VisibilityToggle
                  checked={newSkill.visible}
                  onChange={e => setNewSkill({ ...newSkill, visible: e.target.checked })}
                loading={false}
                />
            </div>
            
            <div className="flex gap-3 mt-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleAddSkill}
                disabled={isSaving || !newSkill.category || !newSkill.name}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {isSaving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                {isSaving ? 'Adding...' : 'Add Skill'}
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setShowAddForm(false);
                  setNewSkill({ category: '', name: '', level: 0, icon_url: '', visible: true });
                  setSelectedCategoryOption(''); // Clear selected category option
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <FaTimes />
                Cancel
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Skills List */}
      <div className="space-y-4">
        {Object.keys(skills).map(category => {
          const categorySkills = skills[category] || [];
          if (categorySkills.length === 0) return null;
          
          return (
            <div key={category} className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                {getCategoryIcon(category)}
                <h4 className="text-lg font-medium text-white">{getCategoryLabel(category)}</h4>
                <span className="text-sm text-gray-400">({categorySkills.length})</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {categorySkills.map((skill, index) => (
                  <motion.div
                    key={skill.id}
                    layout
                    className="bg-gray-600 rounded-lg p-3"
                  >
                    {editingSkill?.id === skill.id ? (
                      <div className="space-y-2">
                        <select
                          value={editCategoryOption}
                          onChange={e => {
                            setEditCategoryOption(e.target.value);
                            if (e.target.value === 'other') {
                              setEditingSkill({ ...editingSkill, category: '' });
                            } else {
                              setEditingSkill({ ...editingSkill, category: e.target.value });
                            }
                          }}
                          className="w-full px-2 py-1 bg-gray-500 border border-gray-400 rounded text-white text-sm focus:outline-none focus:border-primary"
                        >
                          <option value="">Select Category</option>
                          {allCategories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                          <option value="other">Other (Add New Category)</option>
                        </select>
                        {/* Show text input if 'Other' is selected */}
                        {editCategoryOption === 'other' && (
                          <input
                            type="text"
                            value={editingSkill.category}
                            onChange={e => setEditingSkill({ ...editingSkill, category: e.target.value })}
                            className="mt-2 w-full px-2 py-1 bg-gray-500 border border-gray-400 rounded text-white text-sm focus:outline-none focus:border-primary"
                            placeholder="Type new category"
                          />
                        )}
                        <input
                          type="text"
                          value={editingSkill.name}
                          onChange={e => setEditingSkill({ ...editingSkill, name: e.target.value })}
                          className="w-full px-2 py-1 bg-gray-500 border border-gray-400 rounded text-white text-sm focus:outline-none focus:border-primary"
                          placeholder="Skill Name"
                        />
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={editingSkill.level}
                          onChange={e => setEditingSkill({ ...editingSkill, level: parseInt(e.target.value) })}
                          className="w-full"
                        />
                        {/* Icon URL editing can remain as is */}
                        <div className="flex gap-2">
                          <button
                            onClick={handleUpdateSkill}
                            disabled={isSaving || !editingSkill.category || !editingSkill.name || editingSkill.level === undefined}
                            className="flex-1 px-2 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                          >
                            {isSaving ? <FaSpinner className="animate-spin mx-auto" /> : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingSkill(null)}
                            className="flex-1 px-2 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                          >
                            Cancel
                          </button>
                        </div>
                        <VisibilityToggle
                            checked={editingSkill.visible}
                            onChange={e => setEditingSkill({ ...editingSkill, visible: e.target.checked })}
                          loading={false}
                          />
                      </div>
                    ) : (
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <div className="font-medium text-white">{skill.name}</div>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => setEditingSkill(skill)}
                            className="p-1 text-blue-400 hover:text-blue-300"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDeleteSkill(skill.id)}
                            className="p-1 text-red-400 hover:text-red-300"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default SkillsEditor; 